import { memo } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, styled } from "~/common/mui";
import { Link } from "~/common/components/Link";
import {
  HousingSATileSelect,
  TileSelectVariant,
} from "~/features/selectionAssistant/housing/components/Inputs/TileSelect/TileSelect";
import { HousingSACheckboxSelect } from "~/features/selectionAssistant/housing/components/Inputs/CheckboxSelect/CheckboxSelect";
import {
  SASection,
  SASectionContents,
  SASectionDescription,
  SASectionTitle,
} from "~/features/selectionAssistant/housing/components/Section/SASection";
import { SingleDimensionInput } from "~/features/selectionAssistant/housing/components/Inputs/DualUnitNumberInput/SingleDimensionInput";
import { RHFDualSystemNumberInput } from "~/features/selectionAssistant/housing/components/Inputs/DualUnitNumberInput/DualSystemNumberInput";
import { useFormContext } from "react-hook-form";
import { type HousingSearchForm } from "~/features/selectionAssistant/housing/types/housingSAFormTypes";
import { TemperatureSwitch, UnitSwitch } from "~/features/selectionAssistant/housing/components/Filters/Switches";
import { selectDimensionTileConfig } from "~/features/selectionAssistant/housing/redux/selectors";
import { useSelector } from "react-redux";
import { HousingSAAutocompleteInput } from "~/features/selectionAssistant/housing/components/Inputs/HSAAutosuggestInput";
import {
  type HousingSearchFormKey,
  useHSAResetAction,
} from "~/pages/HousingSelectionAssistantPage/HousingSelectionAssistantPage";
import { trackTabsChangedHSA } from "~/app/analytics/ga/ga";
import { useHSAFacetData } from "~/features/selectionAssistant/housing/components/useHSAFacetData";
import { useTranslation } from "react-i18next";
import { NumberSystem } from "~/features/selectionAssistant/housing/types/types";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const FindAssistantButton = styled(Button)(({ theme }) => ({
  backgroundColor: `${theme.palette.common.black} !important`,
  ...theme.typography.bodyMedium,
}));

const FlippedIcon = styled(RestartAltIcon)({
  transform: "scaleX(-1)",
  fontSize: 20,
});

interface Props {
  resetData: () => void;
}

const ResetLink = ({
  fieldsToReset,
  manualReset = false,
}: {
  fieldsToReset: HousingSearchFormKey[];
  manualReset?: boolean;
}) => {
  const { t } = useTranslation();

  const resetData = useHSAResetAction();

  return (
    <Link
      data-test-id={"housing-assistant-reset"}
      className={"mt-auto"}
      onClick={() => resetData(manualReset, ...fieldsToReset)}
    >
      <FlippedIcon className="icon-margin" />
      {t("page.linear.links.reset")}
    </Link>
  );
};

const DesignationSearchMobile = styled("div")({
  "& .MuiFormLabel-filled, & .Mui-focused": {
    whiteSpace: "nowrap",
  },
});

const MobileTypeTileSelect = styled(HousingSATileSelect)(({ theme }) => ({
  "& .MuiGrid-container > div": {
    maxWidth: "100%",
    flexBasis: "100%",
    [theme.breakpoints.up("tablet")]: {
      maxWidth: "50%",
      flexBasis: "50%",
    },
  },
}));

const MobileAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  flexFlow: "column",
  "& > .scha__sa__section:not(:first-child)": {
    marginTop: theme.spacing(3),
  },
  "& .scha__sa__section > .scha__sa__section__contents": {
    padding: 0,
    "&:after": {
      border: "unset",
    },
  },
  "& .scha__housing-selection__dimensions-grid": {
    flexFlow: "column",
    marginLeft: 0,
    "& > *": {
      paddingLeft: 0,
    },
  },
  "& .scha__housing-selection__dimensions-grid__item": {
    flex: 1,
    marginBottom: theme.spacing(2),
    paddingLeft: 0,
  },
  "& .scha__dimensions-inputs__label": {
    marginBottom: 0,
  },
  "& .scha__dimensions-inputs": {
    height: "100%",
  },
  "& .scha__dimensions-inputs-container": {
    display: "inline-flex",
    width: "100%",
    "& > div": {
      flex: 1,
    },
  },
  "& .scha__housing-selection__bearingsealing-grid": {
    flexFlow: "column",
    marginLeft: 0,
    "& > *": {
      paddingLeft: 0,
    },
    "& .scha__tile-select__label": {
      marginBottom: theme.spacing(0.5),
    },
    "& > div:last-child": {
      marginBottom: theme.spacing(4),
    },
    "& .scha__tile-select__tile-container": {
      marginLeft: 0,
      marginTop: 0,
      "& > *": {
        paddingLeft: 0,
        paddingTop: theme.spacing(1.5),
        paddingRight: theme.spacing(1.5),
      },
      "& div": {
        flexBasis: "100%",
        [theme.breakpoints.up("tablet")]: {
          flexBasis: "50%",
        },
      },
    },
  },
}));

const DimensionsGrid = styled("div")(({ theme }) => ({
  display: "flex",
  marginTop: theme.spacing(-3.75),
  marginLeft: theme.spacing(-4),
  width: "auto !important",
  marginBottom: theme.spacing(3.75),
  "& > *": {
    paddingTop: theme.spacing(3.75),
    paddingLeft: theme.spacing(4),
    flex: 1,
  },
}));

const DimensionGridItem = styled("div")({
  flex: 1,
});

const BearingSealingGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(-3.75),
  marginLeft: theme.spacing(-4),
  width: "auto !important",
  "& > *": {
    paddingTop: theme.spacing(3.75),
    paddingLeft: theme.spacing(4),
  },
}));

export const MobileHousingFilters = memo((props: Props) => {
  return <HousingFilters {...props} />;
});

const HousingFilters = ({ resetData }: Props) => {
  const { t } = useTranslation();
  const formProps = useFormContext<HousingSearchForm>();
  const unit = formProps.watch("dimensionUnit");
  const housingTypeState = formProps.watch("housing_type");
  const housingInnerState = formProps.watch("housingdinner");
  const housingOuterState = formProps.watch("housingdouter");
  const searchDesignationState = formProps.watch("housinginclbrg");
  const dimensionsConfig = useSelector(selectDimensionTileConfig);
  const { values: valuesHousingType } = useHSAFacetData({ name: "housing_type" });

  const resetLink = <ResetLink fieldsToReset={["housinginclbrg", "bearing_type", "bearing_design"]} />;

  const renderGaEvent = (
    pushContext:
      | "item_from_designation_search_dropdown_selected"
      | "shaft_diameter_provided"
      | "outside_diameter_provided",
    searchDesignation?: string,
  ) => {
    const selectedHousingType =
      !!valuesHousingType && !!dimensionsConfig
        ? valuesHousingType.filter((item) => item.code === housingTypeState)[0]
        : undefined;
    const selectedHousingTypeLabel =
      !!selectedHousingType && selectedHousingType.label ? selectedHousingType.label : undefined;

    trackTabsChangedHSA(
      "Bearing & Design Envelope",
      pushContext,
      housingInnerState,
      housingOuterState,
      selectedHousingTypeLabel,
      searchDesignation ? searchDesignation : searchDesignationState,
    );
  };

  const submitFuncHelperGA = () => {
    if (housingInnerState.min !== "" || housingInnerState.max !== "") {
      renderGaEvent("shaft_diameter_provided");
    }
    if (housingOuterState.min !== "" || housingOuterState.max !== "") {
      renderGaEvent("outside_diameter_provided");
    }
  };

  const submitButton = (
    <Button data-test-id={"housing-assistant-apply"} type={"submit"} onClick={() => submitFuncHelperGA()}>
      {t("searchPage.refinement.button.apply")}
    </Button>
  );

  const applyAndReset = (
    <div className="mt-auto d-flex flex-align-center flex-justify-between">
      {resetLink}
      {submitButton}
    </div>
  );

  const designationSearch = (
    <DesignationSearchMobile className={"scha__housing-selection__designation-search-mobile"}>
      <HousingSAAutocompleteInput
        name={"housinginclbrg"}
        renderGA={(searchDesignation) =>
          renderGaEvent("item_from_designation_search_dropdown_selected", searchDesignation)
        }
      />
    </DesignationSearchMobile>
  );

  const bearingCharacteristics = (
    <Grid container className={"mt-60"}>
      <Grid
        size={{
          phone: 12,
        }}
      >
        <HousingSACheckboxSelect className={"mb-30"} name={"bearing_type"} multiple={true} />
      </Grid>
      <Grid
        size={{
          phone: 12,
        }}
      >
        <HousingSACheckboxSelect className={"mb-30"} name={"bearing_design"} multiple={true} />
      </Grid>
    </Grid>
  );

  const housingCharacteristics = (
    <>
      <MobileTypeTileSelect className={"mb-30 pt-30 scha__housing-selection__type-mobile"} name={"housing_type"} />

      {!!formProps.watch("housing_type") && (
        <>
          <HousingSATileSelect className={"mb-30"} name={"number_of_bolts"} multiple={true} />

          <MobileTypeTileSelect
            className={"mb-30 scha__housing-selection__type-mobile"}
            name={"housing_shapes"}
            multiple={true}
          />
        </>
      )}

      <Grid container className={"mt-1 pt-30"}>
        <Grid
          size={{
            phone: 6,
          }}
        >
          <HousingSACheckboxSelect className={"mb-30"} name={"housing_design"} multiple={true} />
        </Grid>
      </Grid>
    </>
  );

  return (
    <div>
      <div id="tabs">
        <Accordion>
          <AccordionSummary>{t("page.housingSelectionAssistant.bearing&design")}</AccordionSummary>
          <MobileAccordionDetails className={"scha__accordion-content-mobile"}>
            <SASection className={"scha__housing-selection__section--bearing&sealing"}>
              <SASectionTitle>{t("page.housingSelectionAssistant.bearingSealing")}</SASectionTitle>
              <SASectionDescription>
                {t("page.housingSelectionAssistant.info.bearingSealingCharacteristics")}
              </SASectionDescription>
              <SASectionContents>
                {designationSearch}
                <div className={"mt-1 pt-2"}>
                  <FindAssistantButton
                    href="#dimensions-up"
                    data-test-id={"btn-bore-diameter"}
                    className={"button"}
                    data-ta-spec={"housing_assistant_diameter_scroller"}
                    size={"medium"}
                    startIcon={<ArrowDownwardIcon sx={{ fontSize: 24 }} />}
                  >
                    {t("page.housingselectionassistant.btn.enterBoreDia")}
                  </FindAssistantButton>
                </div>
                {bearingCharacteristics}

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--design"}>
              <SASectionTitle>{t("page.housingSelectionAssistant.designEnvelope")}</SASectionTitle>
              <SASectionDescription>{t("page.housingSelectionAssistant.info.housingType")}</SASectionDescription>

              <SASectionContents>
                {housingCharacteristics}
                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--dimensions"} id="dimensions-up">
              <SASectionTitle>{t("page.selectionAssistant.tab.dimensions")}</SASectionTitle>

              <SASectionContents>
                <UnitSwitch className={"mb-60"} />
                <DimensionsGrid className="scha__housing-selection__dimensions-grid">
                  {dimensionsConfig.map((techFilter) => (
                    <DimensionGridItem key={techFilter.code} className="scha__housing-selection__dimensions-grid__item">
                      <SingleDimensionInput label={techFilter.label} mode={unit} name={techFilter.code} mobile={true} />
                    </DimensionGridItem>
                  ))}
                </DimensionsGrid>

                {applyAndReset}
              </SASectionContents>
            </SASection>
          </MobileAccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>{t("page.housingSelectionAssistant.systemCharacteristics")}</AccordionSummary>
          <MobileAccordionDetails className={"scha__accordion-content-mobile"}>
            <SASection className={"scha__housing-selection__section--system"}>
              <SASectionTitle>{t("page.housingselectionassistant.cluster.system.name")}</SASectionTitle>
              <SASectionDescription>{t("page.housingselectionassistant.cluster.system.subtitle")}</SASectionDescription>
              <SASectionContents>
                <BearingSealingGrid container className={"scha__housing-selection__bearingsealing-grid"}>
                  <Grid>
                    <HousingSATileSelect name={"housingload"} variant={TileSelectVariant.ICN_LEFT} multiple={true} />
                  </Grid>

                  <Grid>
                    <HousingSATileSelect
                      name={"housinglubrication"}
                      variant={TileSelectVariant.ICN_LEFT}
                      multiple={true}
                    />
                  </Grid>

                  <Grid>
                    <HousingSATileSelect name={"housingcoating"} variant={TileSelectVariant.ICN_LEFT} multiple={true} />
                  </Grid>

                  <Grid>
                    <HousingSATileSelect
                      name={"housingmisalignment"}
                      variant={TileSelectVariant.ICN_LEFT}
                      multiple={true}
                    />
                  </Grid>
                </BearingSealingGrid>

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--bearing&sealing"}>
              <SASectionTitle>{t("page.housingSelectionAssistant.bearingSealing")}</SASectionTitle>
              <SASectionDescription>
                {t("page.housingSelectionAssistant.info.bearingSealingCharacteristics")}
              </SASectionDescription>

              <SASectionContents>
                {designationSearch}

                {bearingCharacteristics}

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--housing"}>
              <SASectionTitle>
                {t("page.housingselectionassistant.cluster.housing_characteristics.name")}
              </SASectionTitle>
              <SASectionDescription>
                {t("page.housingselectionassistant.cluster.housing_characteristics.subtitle")}
              </SASectionDescription>

              <SASectionContents>
                <HousingSAAutocompleteInput name={"housinginclhsg"} />

                {housingCharacteristics}

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--temperature"}>
              <SASectionTitle>{t("page.housingselectionassistant.cluster.temperature_range.name")}</SASectionTitle>
              <SASectionDescription>
                {t("page.housingselectionassistant.cluster.temperature_range.subtitle")}
              </SASectionDescription>

              <SASectionContents>
                <TemperatureSwitch className={"mb-30"} />

                <DimensionsGrid className={"scha__housing-selection__dimensions-grid"}>
                  <div>
                    <RHFDualSystemNumberInput
                      mode={NumberSystem.Metric}
                      data-test-id={"housing-sa-temp-min"}
                      label={t("searchPage.refinement.value.label.min")}
                      name={"housingtempmin.min"}
                    />
                  </div>

                  <div>
                    <RHFDualSystemNumberInput
                      mode={NumberSystem.Metric}
                      data-test-id={"housing-sa-temp-max"}
                      label={t("searchPage.refinement.value.label.max")}
                      name={"housingtempmin.max"}
                    />
                  </div>
                </DimensionsGrid>

                {applyAndReset}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--bearing-mounting"}>
              <SASectionTitle>{t("page.housingselectionassistant.cluster.bearing_mounting_type.name")}</SASectionTitle>

              <SASectionContents>
                <HousingSACheckboxSelect
                  className={"mb-30"}
                  omitLabel={true}
                  name={"bearing_mounting_type"}
                  columnAmount={2}
                  multiple={true}
                />

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--housing-material"}>
              <SASectionTitle>{t("page.housingselectionassistant.cluster.housing_material.name")}</SASectionTitle>

              <SASectionContents>
                <HousingSACheckboxSelect
                  className={"mb-30"}
                  omitLabel={true}
                  name={"housing_material"}
                  columnAmount={2}
                  multiple={true}
                />

                {resetLink}
              </SASectionContents>
            </SASection>
          </MobileAccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};
